'use client';

import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckCircle2, AlertCircle, Info, X, Activity } from 'lucide-react';

type ToastType = 'success' | 'error' | 'info';

interface Toast {
    id: string;
    message: string;
    type: ToastType;
}

interface ToastContextType {
    showToast: (message: string, type?: ToastType) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export function ToastProvider({ children }: { children: ReactNode }) {
    const [toasts, setToasts] = useState<Toast[]>([]);

    const showToast = useCallback((message: string, type: ToastType = 'info') => {
        const id = Math.random().toString(36).substring(2, 9);
        setToasts((prev) => [...prev, { id, message, type }]);
        setTimeout(() => {
            setToasts((prev) => prev.filter((t) => t.id !== id));
        }, 5000);
    }, []);

    const removeToast = (id: string) => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
    };

    return (
        <ToastContext.Provider value={{ showToast }}>
            {children}
            <div className="fixed bottom-8 right-8 z-[200] flex flex-col gap-3 pointer-events-none">
                <AnimatePresence>
                    {toasts.map((toast) => (
                        <motion.div
                            key={toast.id}
                            initial={{ opacity: 0, x: 20, scale: 0.9 }}
                            animate={{ opacity: 1, x: 0, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.9, transition: { duration: 0.2 } }}
                            className="pointer-events-auto"
                        >
                            <div className={`
                                flex items-center gap-4 px-6 py-4 rounded-2xl shadow-2xl backdrop-blur-xl border min-w-[320px] max-w-md
                                ${toast.type === 'success' ? 'bg-emerald-50/90 border-emerald-100 text-emerald-900' :
                                  toast.type === 'error' ? 'bg-rose-50/90 border-rose-100 text-rose-900' :
                                  'bg-white/90 border-slate-100 text-slate-900'}
                            `}>
                                <div className={`
                                    w-10 h-10 rounded-xl flex items-center justify-center shrink-0 shadow-sm
                                    ${toast.type === 'success' ? 'bg-emerald-500 text-white' :
                                      toast.type === 'error' ? 'bg-rose-500 text-white' :
                                      'bg-blue-600 text-white'}
                                `}>
                                    {toast.type === 'success' && <CheckCircle2 size={20} strokeWidth={2.5} />}
                                    {toast.type === 'error' && <AlertCircle size={20} strokeWidth={2.5} />}
                                    {toast.type === 'info' && <Activity size={20} strokeWidth={2.5} />}
                                </div>
                                
                                <div className="flex-1">
                                    <p className="text-[10px] font-black uppercase tracking-widest opacity-40 mb-0.5">System Alert</p>
                                    <p className="text-sm font-bold tracking-tight">{toast.message}</p>
                                </div>

                                <button 
                                    onClick={() => removeToast(toast.id)}
                                    className="p-3 -mr-2 hover:bg-black/5 rounded-full transition-colors text-slate-400 dark:text-slate-500 group"
                                    title="Dismiss"
                                >
                                    <X size={16} className="group-hover:scale-110 transition-transform" />
                                </button>
                            </div>
                        </motion.div>
                    ))}
                </AnimatePresence>
            </div>
        </ToastContext.Provider>
    );
}

export function useToast() {
    const context = useContext(ToastContext);
    if (!context) {
        throw new Error('useToast must be used within a ToastProvider');
    }
    return context;
}
